// shared/API.md §46 — names Mattermost cannot hold (plugin `com.satoritech.channelmeta`).
//
// Two things: a group message's shared ALIAS (Mattermost refuses a name on a G channel, so a group
// is otherwise «Ana, Beto, Carla» forever), and admin-defined channel TYPES, each drawn as its own
// sidebar section. The plugin's answer is kept verbatim (normalised) in sync_state, so the list
// renders it from the local store like everything else; this module is the pure part.
//
// Twins: Swift `ViewModels/ChannelMeta.swift`, Kotlin `viewmodels/ChannelMeta.kt`, C# `Logic/ChannelMeta.cs`.
import { channelListSection, type ChannelListSection, type DisplayChannel } from './channelListDisplayModels';

export interface ChannelType {
  /** Stable — later per-type behaviour keys on it; the name can be renamed freely. */
  id: string;
  name: string;
  /** A palette KEY (`COLORS`), never a hex: each client maps it to its own light/dark tokens. */
  color: string;
  sort: number;
}

export interface ChannelMetaState {
  rev: number;
  canManageTypes: boolean;
  types: ChannelType[];
  /** channel id → type id (only channels the reader belongs to). */
  channelTypes: Record<string, string>;
  /** group channel id → alias (only groups the reader belongs to). */
  aliases: Record<string, string>;
}

/** Every write the clients make (the web's worker call `channelMetaWrite`). */
export type ChannelMetaOp =
  | { kind: 'alias'; channelId: string; alias: string }
  | { kind: 'channelType'; channelId: string; typeId: string | null }
  | { kind: 'createType'; name: string; color: string }
  | { kind: 'updateType'; typeId: string; patch: { name?: string; color?: string } }
  | { kind: 'deleteType'; typeId: string }
  | { kind: 'orderTypes'; ids: string[] };

/** `error` = the plugin's code (`duplicate_name`, `forbidden`, …), an HTTP status, or `network`. */
export type ChannelMetaResult = { ok: true; state: ChannelMetaState } | { ok: false; error: string };

/** The message key for a failed write: the two a person can act on, else the generic one. */
export function errorKey(error: string): string {
  if (error === 'duplicate_name') return 'channelMeta.error.duplicate';
  if (error === 'forbidden' || error === '403') return 'channelMeta.error.forbidden';
  return 'channelMeta.error.generic';
}

export const COLORS = ['blue', 'violet', 'green', 'amber', 'red', 'cyan', 'pink', 'slate'] as const;
export const SYNC_STATE_KEY = 'satori_channel_meta';
export const CHANGED_EVENT = 'custom_com.satoritech.channelmeta_changed';
export const TYPE_SECTION_PREFIX = 'type:';
export const TYPE_SECTION_TYPE = 'satori_type';
export const MAX_ALIAS = 64;
export const MAX_TYPE_NAME = 40;

export const EMPTY: ChannelMetaState = { rev: 0, canManageTypes: false, types: [], channelTypes: {}, aliases: {} };

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function stringMap(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) if (k && typeof x === 'string' && x) out[k] = x;
  }
  return out;
}

/** The plugin's JSON (or the stored copy) → a state; anything unusable reads as EMPTY. */
export function parse(raw: unknown): ChannelMetaState {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY;
  const o = raw as Record<string, unknown>;
  const types: ChannelType[] = [];
  if (Array.isArray(o.types)) {
    for (const t of o.types) {
      if (!t || typeof t !== 'object') continue;
      const r = t as Record<string, unknown>;
      const id = str(r.id);
      const name = str(r.name);
      if (!id || !name) continue;
      const color = (COLORS as readonly string[]).includes(str(r.color)) ? str(r.color) : 'slate';
      types.push({ id, name, color, sort: typeof r.sort === 'number' ? r.sort : types.length });
    }
  }
  types.sort((a, b) => a.sort - b.sort);
  return {
    rev: typeof o.rev === 'number' ? o.rev : 0,
    canManageTypes: o.can_manage_types === true,
    types,
    channelTypes: stringMap(o.channel_types),
    aliases: stringMap(o.aliases),
  };
}

/** The stored form (sync_state value): the wire field names, so `decode(encode(x))` round-trips. */
export function encode(state: ChannelMetaState): string {
  return JSON.stringify({
    rev: state.rev,
    can_manage_types: state.canManageTypes,
    types: state.types,
    channel_types: state.channelTypes,
    aliases: state.aliases,
  });
}

export function decode(json: string | null | undefined): ChannelMetaState {
  if (!json) return EMPTY;
  try {
    return parse(JSON.parse(json));
  } catch {
    return EMPTY;
  }
}

/** A group's alias, or null. Only a G channel ever carries one (the plugin refuses the rest). */
export function aliasFor(state: ChannelMetaState, channelId: string): string | null {
  const a = state.aliases[channelId]?.trim();
  return a ? a : null;
}

/** The channel's type, or null (none, or a type deleted since). */
export function typeFor(state: ChannelMetaState, channelId: string): ChannelType | null {
  const id = state.channelTypes[channelId];
  return id ? state.types.find((t) => t.id === id) ?? null : null;
}

export function sectionId(typeId: string): string {
  return TYPE_SECTION_PREFIX + typeId;
}

export function isTypeSectionId(id: string): boolean {
  return id.startsWith(TYPE_SECTION_PREFIX);
}

/** Whitespace collapsed, trimmed — the plugin's own `cleanName`, so the field shows what is saved. */
export function cleanName(s: string): string {
  return s.trim().split(/\s+/).filter(Boolean).join(' ');
}

const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

function newestFirst(a: DisplayChannel, b: DisplayChannel): number {
  return b.lastPostAt - a.lastPostAt || collator.compare(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * The list with one section per channel type (types in their admin order), placed right after
 * Favoritos (first when there is none). A typed public/private channel LEAVES its category for its
 * type's section — except Favoritos, which keeps it (the reader's own pin outranks the admin's
 * grouping). Rows newest activity first, like every list; a type with no channel here is not drawn.
 * Collapse state is this device's own (`collapsedIds`, by `sectionId`).
 */
export function group(sections: readonly ChannelListSection[], state: ChannelMetaState, collapsedIds: ReadonlySet<string>): ChannelListSection[] {
  if (!state.types.length || !Object.keys(state.channelTypes).length) return [...sections];
  const buckets = new Map<string, DisplayChannel[]>();
  const rest: ChannelListSection[] = [];
  for (const section of sections) {
    if (section.type === 'favorites') {
      rest.push(section);
      continue;
    }
    const kept: DisplayChannel[] = [];
    for (const row of section.rows) {
      const type = row.type === 'O' || row.type === 'P' ? typeFor(state, row.id) : null;
      if (!type) {
        kept.push(row);
        continue;
      }
      const bucket = buckets.get(type.id);
      if (bucket) {
        if (!bucket.some((r) => r.id === row.id)) bucket.push(row);
      } else buckets.set(type.id, [row]);
    }
    if (kept.length) rest.push(kept.length === section.rows.length ? section : { ...section, rows: kept });
  }
  if (!buckets.size) return [...sections];
  const typed: ChannelListSection[] = [];
  for (const type of state.types) {
    const rows = buckets.get(type.id);
    if (!rows?.length) continue;
    const id = sectionId(type.id);
    typed.push({
      ...channelListSection({ id, displayName: type.name, type: TYPE_SECTION_TYPE, collapsed: collapsedIds.has(id), rows: [...rows].sort(newestFirst) }),
      typeColor: type.color,
    });
  }
  const favIndex = rest.findIndex((s) => s.type === 'favorites');
  return [...rest.slice(0, favIndex + 1), ...typed, ...rest.slice(favIndex + 1)];
}

function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** A search for a TYPE's name finds every channel of that type, wherever it sits (accents and case ignored). */
export function typeMatchesQuery(state: ChannelMetaState, channelId: string, query: string): boolean {
  const q = fold(query.trim());
  if (q === '') return false;
  const type = typeFor(state, channelId);
  return !!type && fold(type.name).includes(q);
}

// The display-name hook: `ChannelListDerivation.displayName` names a G channel by its alias. Set from
// every sidebar build (the one place the stored state is decoded), read by every surface that names
// a conversation — header, switcher, search, notifications — without threading it through each.
let current: ChannelMetaState = EMPTY;
const listeners = new Set<() => void>();

/** Publishes the state; subscribers hear of a CHANGE on the next microtask (never mid-render). */
export function setCurrent(state: ChannelMetaState): void {
  if (state === current || encode(state) === encode(current)) return;
  current = state;
  queueMicrotask(() => {
    for (const l of listeners) l();
  });
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentState(): ChannelMetaState {
  return current;
}

export function currentAlias(channelId: string): string | null {
  return aliasFor(current, channelId);
}

export const ChannelMeta = {
  parse,
  encode,
  decode,
  aliasFor,
  typeFor,
  sectionId,
  isTypeSectionId,
  cleanName,
  group,
  typeMatchesQuery,
  errorKey,
  setCurrent,
  subscribe,
  currentState,
  currentAlias,
  COLORS,
  SYNC_STATE_KEY,
  CHANGED_EVENT,
  TYPE_SECTION_TYPE,
  MAX_ALIAS,
  MAX_TYPE_NAME,
  EMPTY,
} as const;
